import isNil from 'lodash/isNil';
import maxBy from 'lodash/maxBy';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { GENERAL_POCKET_KEY, isChargeLvo } from '../chargesUtils';
import {
  addCalendarDate,
  type CalendarDate,
  fromCalendarDate,
  isAfter,
  isAfterOrEqual,
  isBeforeOrEqual,
  isWithinInterval,
  subtractCalendarDate,
  toCalendarDate,
} from '../dateutils';
import { getNumberOfMonthsFromFrequency } from '../leaseutils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import { computeInvoicingPeriods, type DatePeriod } from '../periodUtils';
import {
  LeaseVariousOperationType,
  PostingStatus,
  PostingType,
  StatementStatus,
  StatementType,
  VariousOperationType,
} from '../types';

import {
  ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_TOPCLASS,
  isAccountClassOrItsMirror,
} from './accountClasses';
import type {
  AdvancedChargesBilling,
  BilledPeriod,
  CorrectionLease,
  CorrectionLine,
  CorrectionPosting,
  CorrectionRentContext,
  CorrectionStatement,
  CorrectionTransactionSize,
  PeriodMatch,
  RentRunCursors,
} from './billingItemCorrectionTypes';
import { MAX_ATOMIC_TRANSACTION_ITEMS } from './invoiceHelper';
import { resolveSeparateInvoice } from './leaseVariousOperationBillingUtils';

export const MAX_FUTURE_APPLIES_FROM_OPTIONS = 36;

/** Room left under DynamoDB's TransactWriteItems cap for the line update and safety. */
const TRANSACTION_ITEM_HEADROOM = 10;

const MAX_CORRECTION_TRANSACTION_ITEMS = MAX_ATOMIC_TRANSACTION_ITEMS - TRANSACTION_ITEM_HEADROOM;

/**
 * The line update, each stale draft, each unsettled-statement condition, and per document its invoice,
 * one posting per row and the tenant posting.
 */
export const countCorrectionTransactionItems = ({
  documentRowCounts,
  staleDraftCount,
  unsettledStatementCount,
}: CorrectionTransactionSize): number =>
  1 + staleDraftCount + unsettledStatementCount + sumBy(documentRowCounts, (rowCount) => rowCount + 2);

export const isCorrectionTooLong = (size: CorrectionTransactionSize): boolean =>
  countCorrectionTransactionItems(size) > MAX_CORRECTION_TRANSACTION_ITEMS;

const TURNOVER_CLASS_RANGE = 100000;

export const isTurnoverClass = (postingClass: number): boolean =>
  postingClass >= ACCOUNT_TURNOVER_TOPCLASS && postingClass < ACCOUNT_TURNOVER_TOPCLASS + TURNOVER_CLASS_RANGE;

export const isRentRidingCharge = (line: CorrectionLine): boolean => isChargeLvo(line) && !resolveSeparateInvoice(line);

// Own-document charges post on 700400 / 700600 too: only the line's routing tells a rent-riding posting apart.
export const isRentRidingChargePosting = (line: CorrectionLine, postingClass: number): boolean =>
  isRentRidingCharge(line) &&
  (isAccountClassOrItsMirror(postingClass, ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS) ||
    isAccountClassOrItsMirror(postingClass, ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS));

export const getChargedSign = (line: CorrectionLine): number =>
  line.operationType === LeaseVariousOperationType.DISCOUNT ? -1 : 1;

export const getTenantChargedAmount = (posting: CorrectionPosting): number => {
  const amount = posting.amountVatExcluded ?? posting.totalAmount;
  return posting.type === PostingType.CREDIT ? amount : -amount;
};

export const isPeriodHolding: PeriodMatch = (period, periodFrom, periodTo) =>
  isBeforeOrEqual(period.periodFrom, periodFrom) && isAfterOrEqual(period.periodTo, periodTo);

export const isPeriodOverlapping: PeriodMatch = (period, periodFrom, periodTo) =>
  isBeforeOrEqual(period.periodFrom, periodTo) && isAfterOrEqual(period.periodTo, periodFrom);

export const isLineDraftPosting = (posting: CorrectionPosting, lineId: string): boolean =>
  posting.status === PostingStatus.DRAFT &&
  posting.leaseVariousOperationId === lineId &&
  isTurnoverClass(posting.class);

export const computeSchedulePeriods = (schedule: CorrectionLease): DatePeriod[] =>
  computeInvoicingPeriods(
    toCalendarDate(schedule.startDate),
    toCalendarDate(schedule.endDate),
    schedule.paymentInvoicePeriod,
    schedule.paymentFrequency,
    schedule.paymentInvoicePeriodCustomDay ?? undefined
  );

export const findPeriodIndex = (periods: DatePeriod[], date: CalendarDate): number =>
  periods.findIndex((period) => isWithinInterval(date, { start: period.periodFrom, end: period.periodTo }));

export const resolveAmountAt = (line: CorrectionLine, date: CalendarDate): number =>
  (line.leaseVariousOperationHistories ?? []).find(
    (history) =>
      isBeforeOrEqual(toCalendarDate(history.periodFrom), date) &&
      isAfterOrEqual(toCalendarDate(history.periodTo), date)
  )?.amount ?? line.amount;

export const isAmountChanged = (line: CorrectionLine, newAmount: number): boolean =>
  roundAtSecondDecimal(newAmount) !== roundAtSecondDecimal(line.amount);

/** A provision cannot be corrected inside a validated tenant statement: that period is settled. */
export const getCorrectionMinDate = (line: CorrectionLine, statements: CorrectionStatement[]): CalendarDate => {
  const lineStart = toCalendarDate(line.startDate);
  if (line.operationType !== LeaseVariousOperationType.PROVISIONED_CHARGE) {
    return lineStart;
  }
  // The pocket can move between general and dedicated after a statement: either identity may have settled it
  const pocketKeys = [GENERAL_POCKET_KEY, line.id];
  const lastSettled = maxBy(
    statements.filter(
      (statement) =>
        statement.status === StatementStatus.DONE &&
        (isNilOrEmpty(statement.types) || statement.types.includes(StatementType.CHARGES)) &&
        (isNilOrEmpty(statement.selectedPocketKeys) ||
          statement.selectedPocketKeys.some((pocketKey) => pocketKeys.includes(pocketKey)))
    ),
    (statement) => statement.periodTo
  );
  if (isNil(lastSettled)) {
    return lineStart;
  }
  const dayAfterSettlement = addCalendarDate(toCalendarDate(lastSettled.periodTo), 1, 'days');
  return isAfter(dayAfterSettlement, lineStart) ? dayAfterSettlement : lineStart;
};

/** A one-time provision's billed period is reached by a validated tenant statement: its amount can no longer change. */
export const isOneTimeProvisionSettled = (
  line: CorrectionLine,
  billedPeriods: BilledPeriod[],
  statements: CorrectionStatement[]
): boolean => {
  const firstBilledPeriod = billedPeriods[0];
  if (
    line.operationType !== LeaseVariousOperationType.PROVISIONED_CHARGE ||
    line.type !== VariousOperationType.ONE_TIME ||
    isNil(firstBilledPeriod)
  ) {
    return false;
  }
  const { periodFrom } = firstBilledPeriod;
  // A statement prorates the posting over its billed period: one ending inside it settled part of the amount
  return isAfter(getCorrectionMinDate({ ...line, startDate: fromCalendarDate(periodFrom) }, statements), periodFrom);
};

export const getUnsettledStatements = <Statement extends Pick<CorrectionStatement, 'status'>>(
  statements: Statement[]
): Statement[] => statements.filter((statement) => statement.status !== StatementStatus.DONE);

export const getCorrectionMinEndDate = (line: CorrectionLine, statements: CorrectionStatement[]): CalendarDate =>
  subtractCalendarDate(getCorrectionMinDate(line, statements), 1, 'days');

export const getAppliesFromOptions = ({
  billedPeriods,
  nextUnbilledFrom,
  line,
  lease,
  minDate,
}: {
  billedPeriods: BilledPeriod[];
  nextUnbilledFrom: CalendarDate;
  line: CorrectionLine;
  lease: CorrectionRentContext;
  minDate: CalendarDate;
}): CalendarDate[] => {
  const lineEnd = toCalendarDate(line.endDate);
  const frequencyMonths = getNumberOfMonthsFromFrequency(
    isRentRidingCharge(line) ? lease.chargeSchedule.paymentFrequency : line.paymentFrequency
  );
  const isWholeMonthCadence = frequencyMonths >= 1;
  const futureCount = isWholeMonthCadence ? MAX_FUTURE_APPLIES_FROM_OPTIONS : 1;
  const futureStarts = Array.from({ length: futureCount }, (_, index) =>
    addCalendarDate(nextUnbilledFrom, index * frequencyMonths, 'months')
  ).filter((start) => isBeforeOrEqual(start, lineEnd));
  return uniqBy([...billedPeriods.map((period) => period.periodFrom), ...futureStarts], (start) =>
    fromCalendarDate(start)
  ).filter((start) => isAfterOrEqual(start, minDate));
};

export const getCorrectionRentContext = (
  lease: CorrectionLease & RentRunCursors & { advancedChargesInvoices?: AdvancedChargesBilling | null },
  unitLeases: { unitId: string; startDate?: string | null; endDate?: string | null; mainUnit?: boolean | null }[],
  unitId: string
): CorrectionRentContext => {
  const unitLease = unitLeases.find((candidate) => candidate.unitId === unitId);
  const chargeCadence = isNilOrEmpty(lease.advancedChargesInvoices?.invoiceSenderId)
    ? lease
    : (lease.advancedChargesInvoices ?? lease);
  return {
    paymentFrequency: lease.paymentFrequency,
    paymentInvoicePeriod: lease.paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay: lease.paymentInvoicePeriodCustomDay,
    startDate: lease.startDate,
    endDate: lease.endDate,
    unitLease: {
      startDate: unitLease?.startDate ?? lease.startDate,
      endDate: unitLease?.endDate ?? lease.endDate,
      mainUnit: unitLease?.mainUnit ?? true,
    },
    chargeSchedule: {
      paymentFrequency: chargeCadence.paymentFrequency,
      paymentInvoicePeriod: chargeCadence.paymentInvoicePeriod,
      paymentInvoicePeriodCustomDay: chargeCadence.paymentInvoicePeriodCustomDay,
      startDate: lease.startDate,
      endDate: lease.endDate,
      lastInvoiceDate: chargeCadence.lastInvoiceDate,
      lastDraftInvoiceDate: chargeCadence.lastDraftInvoiceDate,
    },
  };
};

export const isIndexedFixedCharge = (
  line: Pick<CorrectionLine, 'operationType' | 'unitId'>,
  unitLeases: { unitId: string; chargesIndexation?: boolean | null }[]
): boolean =>
  line.operationType === LeaseVariousOperationType.FIXED_CHARGE &&
  unitLeases.some((unitLease) => unitLease.unitId === line.unitId && unitLease.chargesIndexation === true);

export const getLineDraftInvoiceIds = (postings: CorrectionPosting[], lineId: string): string[] =>
  uniq(
    postings.reduce<string[]>(
      (invoiceIds, posting) =>
        isLineDraftPosting(posting, lineId) && !isNil(posting.invoiceId)
          ? [...invoiceIds, posting.invoiceId]
          : invoiceIds,
      []
    )
  );
